import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useAuth, homeFor } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { BackButton, InstallBanner, InstallMenuButton } from "../pwa/PwaParts";

const NAV = [
  { to: "/", label: "Accueil", end: true, id: "home" },
  { to: "/comment-ca-marche", label: "Comment ça marche", id: "how" },
  { to: "/entreprises", label: "Annuaire", id: "search" },
  { to: "/suivi", label: "Suivre un dossier", id: "track" },
];

export const Logo = ({ light = false, compact = false }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2" aria-label="VOXA AFRIKA — accueil">
    <span className={`grid h-10 w-10 shrink-0 place-items-center ${light ? "rounded-xl p-0.5" : ""}`} style={light ? { backgroundColor: "#ffffff" } : undefined}>
      <img src="/logo-icon.png" alt="" aria-hidden="true" className="h-full w-full object-contain" data-testid="logo-icon" />
    </span>
    {!compact && <span className={`whitespace-nowrap font-display text-lg font-extrabold tracking-tight ${light ? "text-white" : "text-[#0B5A3C]"}`}>
      VOXA <span className={light ? "text-[#F2B824]" : "text-[#C9921A]"}>AFRIKA</span>
    </span>}
  </Link>
);

const AuthActions = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) {
    return (
      <>
        <Button variant="ghost" asChild data-testid="nav-login-link"><Link to="/connexion" onClick={onNavigate}>Connexion</Link></Button>
        <Button asChild className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="nav-company-signup-link">
          <Link to="/inscription-entreprise" onClick={onNavigate}>Espace entreprise</Link>
        </Button>
      </>
    );
  }
  return (
    <>
      <Button variant="ghost" asChild data-testid="nav-my-space-link"><Link to={homeFor(user)} onClick={onNavigate}>Mon espace</Link></Button>
      <Button variant="outline" className="rounded-full" data-testid="logout-button"
        onClick={async () => { await logout(); navigate("/"); }}>Déconnexion</Button>
    </>
  );
};

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const linkCls = ({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-blue-600" : "text-slate-600 hover:text-slate-900"}`;
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1"><BackButton /><Logo /></div>
          <nav className="hidden items-center gap-8 md:flex">
            {NAV.map((n) => <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`nav-${n.id}-link`}>{n.label}</NavLink>)}
          </nav>
          <div className="hidden items-center gap-2 md:flex"><AuthActions /></div>
          <button className="grid h-11 w-11 place-items-center rounded-full md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu" aria-expanded={open}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-4 py-4 md:hidden">
            {NAV.map((n) => <NavLink key={n.to} to={n.to} end={n.end} className={(s) => `${linkCls(s)} flex min-h-11 items-center`} onClick={() => setOpen(false)}>{n.label}</NavLink>)}
            <div className="flex flex-wrap gap-2 pt-2"><AuthActions onNavigate={() => setOpen(false)} /><InstallMenuButton /></div>
          </div>
        )}
      </header>
      <main className="flex-1"><Outlet /></main>
      <InstallBanner />
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-10 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <Logo />
          <div className="flex flex-wrap gap-6">
            <Link to="/entreprises" className="hover:text-slate-900" data-testid="footer-search-link">Rechercher une entreprise</Link>
            <Link to="/suivi" className="hover:text-slate-900" data-testid="footer-track-link">Suivre un dossier</Link>
            <Link to="/inscription-entreprise" className="hover:text-slate-900" data-testid="footer-company-link">Créer un espace entreprise</Link>
            <Link to="/contact" className="hover:text-slate-900" data-testid="footer-contact-link">Contact</Link>
            <Link to="/confidentialite" className="hover:text-slate-900" data-testid="footer-privacy-link">Confidentialité</Link>
            <Link to="/conditions" className="hover:text-slate-900" data-testid="footer-terms-link">Conditions d'utilisation</Link>
          </div>
          <p>© {new Date().getFullYear()} VOXA AFRIKA</p>
        </div>
      </footer>
    </div>
  );
}
